import React, { useEffect, useState } from "react";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useProperties } from "../context/PropertyContext";
import PropertySelector from "../components/PropertySelector";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

function today() { return new Date().toISOString().slice(0, 10); }

const CATEGORIES = ["maintenance", "utilities", "cleaning", "supplies", "salaries", "marketing", "other"];

export default function Finances() {
  const { t } = useLang();
  const { format: money } = useCurrency();
  const { properties, currentPropertyId, approvedProperties } = useProperties();
  const [expenses, setExpenses] = useState([]);
  const [form, setForm] = useState({ property_id: "", date: today(), category: "utilities", amount: "", notes: "" });

  const load = async () => {
    try {
      const params = currentPropertyId ? { property_id: currentPropertyId } : {};
      const e = await api.get("/expenses", { params });
      setExpenses(e.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentPropertyId]);

  useEffect(() => {
    if (approvedProperties[0] && !form.property_id) {
      setForm((f) => ({ ...f, property_id: currentPropertyId || approvedProperties[0].id }));
    }
  }, [currentPropertyId, approvedProperties]);

  const add = async (e) => {
    e.preventDefault();
    // Ensure property_id is set (use current or first approved property as fallback)
    const propertyId = form.property_id || currentPropertyId || approvedProperties[0]?.id;
    if (!propertyId) return toast.error(t("fields_required"));
    if (!form.category || !form.amount) return toast.error(t("fields_required"));
    try {
      const { data } = await api.post("/expenses", { ...form, property_id: propertyId, amount: Number(form.amount) });
      setExpenses([data, ...expenses]);
      setForm({ ...form, property_id: propertyId, amount: "", notes: "" });
      toast.success(t("saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      setExpenses(expenses.filter((x) => x.id !== id));
      toast.success(t("deleted"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="finances-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold">{t("finances")}</h1>
          <p className="text-sm text-[#5C6053] mt-1">{t("add_expense")}</p>
        </div>
        {properties.length > 1 && <PropertySelector />}
      </div>

      <form onSubmit={add} className="bg-white border border-[#E5E1D8] rounded-2xl p-4 sm:p-6 shadow-sm grid grid-cols-1 sm:grid-cols-5 gap-3">
        <select
          data-testid="expense-category-select"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="border border-[#E5E1D8] rounded-lg px-3 py-2 text-sm bg-[#F9F6F0]"
        >
          {CATEGORIES.map((c) => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}
        </select>
        <input
          data-testid="expense-amount-input"
          type="number"
          placeholder={t("amount")}
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          className="border border-[#E5E1D8] rounded-lg px-3 py-2 text-sm bg-[#F9F6F0]"
        />
        <input
          data-testid="expense-date-input"
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          className="border border-[#E5E1D8] rounded-lg px-3 py-2 text-sm bg-[#F9F6F0]"
        />
        <input
          data-testid="expense-notes-input"
          placeholder={t("notes")}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="border border-[#E5E1D8] rounded-lg px-3 py-2 text-sm bg-[#F9F6F0]"
        />
        <button data-testid="expense-add-btn" type="submit" className="brand-btn rounded-lg text-sm py-2 inline-flex items-center justify-center gap-1">
          <Plus size={16} /> {t("add_expense")}
        </button>
      </form>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl shadow-sm overflow-hidden">
        {expenses.length === 0 ? (
          <div className="p-8 text-center text-[#5C6053] text-sm">{t("no_expenses")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase">
              <tr>
                <th className="text-start px-4 py-3">{t("date")}</th>
                <th className="text-start px-4 py-3">{t("category")}</th>
                <th className="text-start px-4 py-3">{t("amount")}</th>
                <th className="text-start px-4 py-3">{t("notes")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-t border-[#E5E1D8]">
                  <td className="px-4 py-3">{e.date}</td>
                  <td className="px-4 py-3">{t(`cat_${e.category}`)}</td>
                  <td className="px-4 py-3">{money(e.amount)}</td>
                  <td className="px-4 py-3 text-[#5C6053]">{e.notes}</td>
                  <td className="px-4 py-3 text-end">
                    <button data-testid={`expense-delete-${e.id}`} onClick={() => remove(e.id)} className="text-[#C96A52] hover:bg-[#FBE9E4] p-2 rounded">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
